if (!customElements.get('bitig-quantity-picker')) {
  customElements.define('bitig-quantity-picker', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (this.dataset.ready) return;
        this.input = this.querySelector('input.quantity__input');
        if (!this.input) return;
        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.className = 'bitig-quantity-picker__trigger';
        this.button.setAttribute('role', 'combobox');
        this.button.setAttribute('aria-haspopup', 'listbox');
        this.button.setAttribute('aria-expanded', 'false');
        const label = document.createElement('span');
        label.textContent = this.dataset.label + ':';
        this.value = document.createElement('span');
        this.button.append(label, this.value);
        this.menu = document.createElement('div');
        this.menu.className = 'bitig-quantity-picker__menu';
        this.menu.id = this.input.id + '-options';
        this.menu.setAttribute('role', 'listbox');
        this.menu.setAttribute('aria-label', this.dataset.label);
        this.menu.hidden = true;
        this.button.setAttribute('aria-controls', this.menu.id);
        this.prepend(this.button, this.menu);
        this.dataset.ready = 'true';
        this.button.addEventListener('click', () => this.setOpen(this.menu.hidden));
        this.button.addEventListener('keydown', event => {
          const key = event.key;
          if (key === 'Escape') { this.setOpen(false); return; }
          if (key === 'Tab') { this.setOpen(false); return; }
          if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) {
            event.preventDefault();
            if (this.menu.hidden) { this.setOpen(true); return; }
            if (key === 'Enter' || key === ' ') { this.choose(this.options[this.active]?.dataset.value); return; }
            const next = key === 'Home' ? 0 : key === 'End' ? this.options.length - 1 : this.active + (key === 'ArrowDown' ? 1 : -1);
            this.highlight(Math.max(0, Math.min(this.options.length - 1, next)));
          } else if (/^[0-9]$/.test(key)) {
            if (this.menu.hidden) this.setOpen(true);
            const index = this.options.findIndex(option => option.dataset.value === key);
            if (index >= 0) this.highlight(index);
          }
        });
        this.menu.addEventListener('pointerdown', event => event.preventDefault());
        this.menu.addEventListener('click', event => {
          const option = event.target.closest('[role="option"]');
          if (option) this.choose(option.dataset.value);
        });
        this.outside = event => { if (!this.contains(event.target)) this.setOpen(false); };
        document.addEventListener('pointerdown', this.outside);
        this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.setOpen(false); });
        this.input.addEventListener('change', () => this.sync());
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.input, { attributes: true, attributeFilter: ['min', 'max', 'step', 'data-cart-quantity', 'data-inventory-quantity', 'data-inventory-policy', 'data-inventory-management', 'data-available', 'disabled'] });
        this.sync();
      });
    }
    disconnectedCallback() { this.observer?.disconnect(); document.removeEventListener('pointerdown', this.outside); }
    setOpen(open) {
      if (!this.menu || (open && this.button.disabled)) return;
      this.menu.hidden = !open;
      this.button.setAttribute('aria-expanded', String(open));
      if (open) this.highlight(Math.max(0, this.options.findIndex(option => option.dataset.value === this.input.value)));
      else this.button.removeAttribute('aria-activedescendant');
    }
    highlight(index) {
      this.active = index;
      this.options.forEach((option, i) => option.classList.toggle('is-active', i === index));
      const active = this.options[index];
      if (active) {
        this.button.setAttribute('aria-activedescendant', active.id);
        this.menu.scrollTop = Math.max(0, active.offsetTop - this.menu.clientHeight + active.offsetHeight);
      }
    }
    choose(value) {
      if (!value) return;
      this.setOpen(false);
      this.toggleAttribute('data-manual', value === 'custom');
      if (value === 'custom') { this.input.focus(); return; }
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.button.focus();
    }
    sync() {
      const min = Number(this.input.min) || 1, step = Number(this.input.step) || 1;
      const limited = this.input.dataset.inventoryManagement && this.input.dataset.inventoryPolicy === 'deny';
      const remaining = limited ? Math.max(0, Number(this.input.dataset.inventoryQuantity) - Number(this.input.dataset.cartQuantity || 0)) : Infinity;
      const max = Math.min(this.input.max ? Number(this.input.max) : Infinity, remaining);
      let current = Number(this.input.value) || min;
      this.menu.replaceChildren();
      const addOption = (value, text) => {
        const option = document.createElement('div');
        option.id = this.menu.id + '-' + value;
        option.setAttribute('role', 'option');
        option.dataset.value = String(value);
        option.textContent = String(text);
        this.menu.append(option);
      };
      const end = Math.min(max, min + step * 99);
      for (let n = min; n <= end; n += step) addOption(n, n);
      if (current > end && current <= max) addOption(current, current);
      if (max > end) addOption('custom', this.dataset.more);
      this.button.disabled = this.input.disabled || this.input.dataset.available === 'false' || max < min;
      if (!this.button.disabled && (current < min || current > max)) { current = min; this.input.value = String(min); }
      this.value.textContent = this.button.disabled ? '—' : String(current);
      this.button.setAttribute('aria-label', this.dataset.label + ': ' + this.value.textContent);
      this.options = [...this.menu.children];
      this.options.forEach(option => option.setAttribute('aria-selected', String(option.dataset.value === String(current))));
      if (!this.menu.hidden) this.setOpen(false);
      const note = this.closest('[id^="ProductInfo-"]')?.querySelector('[data-bitig-stock]');
      if (note) {
        const available = this.input.dataset.available !== 'false';
        const status = note.querySelector('[data-stock-status]');
        status.textContent = available ? note.dataset.inStock : note.dataset.unavailable;
        status.classList.toggle('bitig-stock-status--unavailable', !available);
        const count = note.querySelector('[data-stock-count]');
        count.hidden = !available || !this.input.dataset.inventoryManagement || Number(this.input.dataset.inventoryQuantity) <= 0;
        count.querySelector('strong').textContent = this.input.dataset.inventoryQuantity;
      }
    }
  });
}
